
import GoogleAutocompleteMixin from "~/mixin/google-autocomplete-mixin.js";
import { butter } from "~/plugins/buttercms";
import FormatMixin from "~/mixin/format-mixin.js";
import ProcessLineItemsMixin from "~/mixin/process-lineitems-mixin";
import dayjs from "dayjs";
import CalDropdownInput from "../cal-dropdown-input.vue";

const datadogErrorUrl = "datadog/events/error";

export default {
  components: { CalDropdownInput },
  name: "cal-recipient-details",
  mixins: [GoogleAutocompleteMixin, FormatMixin, ProcessLineItemsMixin],
  props: {
    workflow: {
      type: String,
      required: false,
    },
    config: {
      type: Object,
      required: false,
    },
    isReLoadRecipientDialog: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      completedRecipientForm: false,
      isSameAddressOrder: false,
      recipient: { ...this.$store.state.cartItem.recipient },
      pageData: {},
      deliveryTown: { ...this.$store.state.cartItem.deliveryTown },
      product: this.$store.state.cartItem.product,
      postalCodeSupport: true,
      shopifyDeliveryWindows: [],
      deliveryWindows: [],
      postalCodeValid: false, // BUG_FIX: for session-id b408764f-0aba-4ffe-9e33-74ad2473fa63
      errorHTML: `${this.$t(
        "alert_message_delivery_rates_issue"
      )} <a class="text-white underline" href="tel:(888) 618-1211"> ${this.$t(
        "calli_contact_number"
      )} </a>${this.$t("alert_message_delivery_rates_issue_check_text")}`,
      businessName: "",
    };
  },
  async created() {
    await this.validatePostalCode();
    await this.validateRecipientDetails();
    this.$nuxt.$on("toggleSameAddressOrder", (isSameAddressOrder) => {
      this.isSameAddressOrder = isSameAddressOrder;
      if (isSameAddressOrder) {
        this.recipient = { ...this.$store.state.cartItem.recipient };
        for (const key of Object.keys(this.$store.state.cartItem.recipient)) {
          this.$set(
            this.recipient,
            key,
            this.$store.state.cartItem.recipient[key]
          );
        }
      }
    });
    this.$nuxt.$on("toggleSameAddress", () => {
      this.recipient = { ...this.$store.state.cartItem.recipient };
      for (const key of Object.keys(this.$store.state.cartItem.recipient)) {
        this.$set(
          this.recipient,
          key,
          this.$store.state.cartItem.recipient[key]
        );
      }
    });

    this.$nuxt.$on("scrollToRecipientDetails", () => {
      const recipientDetailsEl = document.getElementById('recipient-details');
      if (recipientDetailsEl) {
        recipientDetailsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  },
  mounted() {
    this.isSameAddressOrder =
      this.workflow !== "SUBSCRIPTION" &&
      this.$store.state.cartItem.sameDayDelivery.isSameAddressOrder;
    this.googleAutoComplete(this.$refs.autocomplete);
  },
  computed: {
    isMobile() {
      return window.matchMedia("(max-width: 768px)").matches;
    },
  },
  watch: {
    async isReLoadRecipientDialog() {
      this.deliveryTown = { ...this.$store.state.cartItem.deliveryTown };
      if (!this.isObjectNotEmpty(this.deliveryTown)) {
        this.deliveryTown = sessionStorage.deliveryTown
          ? JSON.parse(sessionStorage.getItem(sessionStorage.deliveryTown))
          : {};
      }
      await this.validateRecipientDetails();
    },
    async "recipient.firstName"() {
      await this.validateRecipientDetails();
      this.$nuxt.$emit("firstNameUpdated", this.recipient.firstName);
    },
    async "recipient.lastName"() {
      await this.validateRecipientDetails();
    },
    async "recipient.address"() {
      await this.validateRecipientDetails();
    },
    async "recipient.city"() {
      await this.validateRecipientDetails();
    },
    async "recipient.postalCode"() {
      await this.validatePostalCode();
      await this.validateRecipientDetails();
      this.$nuxt.$emit("postalCodeUpdated", this.recipient.postalCode);
    },
    async "recipient.province"() {
      await this.validateRecipientDetails();
    },
    async "recipient.apartment"() {
      await this.validateRecipientDetails();
    },
    async "recipient.addressType"() {
      await this.validateRecipientDetails();
      this.$nuxt.$emit("addressTypeUpdated");
    },
    async "recipient.deliveryNote"() {
      await this.validateRecipientDetails();
    },
    async "recipient.buzzerCode"() {
      await this.validateRecipientDetails();
    },
    async "recipient.businessName"() {
      await this.validateRecipientDetails();
    },
  },
  async fetch() {
    try {
      const page = await butter(this.$config).page.retrieve(
        "recipient_details",
        "recipient-details",
        {
          locale: this.$i18n.locale,
          timeout: 10000,
        }
      );
      this.pageData = page?.data.data.fields;
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    updateDeliveryNotes(notes) {
      this.recipient.deliveryNote = notes;
    },
    onChangeProvince(event) {
      this.deliveryTown.province = event.target.value;
    },
    onChangeAddressType(event) {
      this.recipient.addressType = event.target.value;
    },
    async validatePostalCode() {
      // this.$nuxt.$emit("disableContinue", true);
      this.$nuxt.$emit("recipientDetailsDisabled", false);
      if (
        this.recipient.postalCode &&
        (await this.postalCodeValidationForDeliveryRates(
          this.recipient.postalCode
        ))
      ) {
        this.postalCodeValid = true;
      } else {
        this.postalCodeValid = false;
      }
    },
    async validateRecipientDetails() {
      this.recipient.city = this.recipient.city || this.deliveryTown.name;
      this.recipient.province =
        this.recipient.province || this.deliveryTown.province;
      const isBusinessName =
        this.recipient.addressType === this.$t("business_text")
          ? !!this.recipient.businessName
          : true;

      if (this.recipient.addressType !== this.$t("business_text")) {
        this.recipient.businessName = "";
        if (
          this.recipient.deliveryNote &&
          this.recipient.deliveryNote.includes("Business Name:")
        ) {
          let deliveryNote = this.recipient.deliveryNote.split("-")[0];
          this.recipient.deliveryNote = deliveryNote ? deliveryNote : "";
        }
      }

      if (
        this.recipient.addressType === this.$t("business_text") &&
        this.recipient.businessName
      ) {
        const businessNote = `Business Name: ${this.recipient.businessName}`;
        if (!this.recipient.deliveryNote) {
          this.recipient.deliveryNote += `-> ${businessNote}`;
        }
        if (this.recipient.deliveryNote.includes("Business Name:")) {
          this.recipient.deliveryNote = this.recipient.deliveryNote.replace(
            /Business Name:.*$/,
            `${businessNote}`
          );
        } else {
          this.recipient.deliveryNote += `-> ${businessNote}`;
        }
      }

      this.$store.dispatch("cartItem/RECIPIENT_ADD", { ...this.recipient });

      if (
        this.postalCodeValid &&
        this.recipient.firstName &&
        this.recipient.lastName &&
        this.recipient.address &&
        this.recipient.city &&
        this.recipient.province &&
        this.recipient.addressType &&
        !/^\s+$/.test(this.recipient.firstName) &&
        !/^\s+$/.test(this.recipient.lastName) &&
        !/^\s+$/.test(this.recipient.address) &&
        !/^\s+$/.test(this.recipient.city) &&
        !/^\s+$/.test(this.recipient.postalCode) &&
        isBusinessName
      ) {
        // if (this.workflow == 'SUBSCRIPTION') {
        //   this.$nuxt.$emit("disableContinueSubs", false);
        // } else {
        //   this.$nuxt.$emit("disableContinue", false);
        // }
        this.$nuxt.$emit("recipientDetailsDisabled", false);
      } else {
        // if (this.workflow == 'SUBSCRIPTION') {
        //   this.$nuxt.$emit("disableContinueSubs", true);
        // } else {
        //   this.$nuxt.$emit("disableContinue", true);
        // }
        this.$nuxt.$emit("recipientDetailsDisabled", true);
      }
      this.$nuxt.$emit("recipientDetailsUpdated");
    },
    async postalCodeValidationForDeliveryRates(postal) {
      this.postalCodeSupport =
        this.isPostalCodeValid(postal) &&
        (await this.areDeliveryRatesAvailable());
      return this.postalCodeSupport;
    },
    populateForm(recipient) {
      if (!recipient) return;

      this.recipient.firstName = recipient.firstName;
      this.recipient.lastName = recipient.lastName;
      this.recipient.address = recipient.address;
      this.recipient.city = recipient.city;
      this.recipient.province = recipient.province;
      this.recipient.phone = recipient.phone;
      this.recipient.addressType = recipient.addressTypeModel;
      this.recipient.postalCode = recipient.postal;
      this.recipient.apartment = recipient.apartment;
      this.recipient.deliveryNote = recipient.delivery;
    },
    getDeliveryMessage() {
      const messageContainer = this.pageData["cal-delivery-note-message"];

      if (messageContainer && this.recipient.addressType) {
        const addressTypeStripped = this.recipient.addressType
          .normalize("NFD")
          .replace(/[^\p{L}\d]+/gu, "")
          .toLowerCase();
        const message = messageContainer.delivery_message_list.find(
          (message) => message.address_type === addressTypeStripped
        );
        return message ? message.delivery_message : "";
      }
    },
    async areDeliveryRatesAvailable() {
      let postal = this.recipient.postalCode.replace(/\s/g, "").trim().toUpperCase();

      if (this.product && !this.product.verif_sku) {
        this.product.verif_sku = this.product.sku;
      }

      if (this.product && this.product?.verif_sku?.endsWith("_ca")) {
        try {
          let firstAvailableDate = this.$dayjs().format("YYYY-MM-DD");

          const payload = await this.$axios.$get(
            `https://zetfwunfcd.execute-api.ca-central-1.amazonaws.com/prod/rates/${postal}?user_date=${firstAvailableDate}`,
            {
              headers: {},
            }
          );

          await this.logDataDogEvent("CHECK-RATES-CA-RESULT");

          return payload.length > 0;
        } catch (e) {
          await this.logDataDogEvent("CHECK-RATES-CA-ERROR");
          return false;
        }
      } else {
        try {
          this.deliveryRatesLoadingState = { loading: true, success: false, fail: false };

          let delDate = sessionStorage.deliveryDate;
          if (delDate) {
            delDate = this.$dayjs(delDate).format("YYYY-MM-DD");
            sessionStorage.setItem("deliveryDate", delDate);
          }

          const payload = await this.$axios.$get(
            `/rates/delivery/postalcode/${postal}/date/${delDate}?lang=${this.$i18n.getLocaleCookie()}`,
            { headers: { "callia-lang": this.$i18n.getLocaleCookie() } }
          );

          const finalPayload = JSON.parse(JSON.parse(payload.body));

          await this.logDataDogEvent("CHECK-RATES-RESULT");

          return finalPayload.length > 0;
        } catch (error) {
          await this.logDataDogEvent("DELIVERY-RATES-NOT-FOUND");

          return false;
        }
      }
    },
    isObjectNotEmpty(obj) {
      return Object.keys(obj).length > 0;
    },
    // TODO: Move this code into a shared file if this method need in any other place.
    async logDataDogEvent(name) {
      try {
        await this.$axios.$post(datadogErrorUrl, {
          error: {
            name,
            ddSessionId: this.getatadogSessionID()
          },
        });
      } catch (err) {
        console.error("Data Dog logging failed:", err);
      }
    }
  },
};
